import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { Options, V2AdminUpdateHelpArticleData } from '@workspace/api-backend'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminDeleteHelpArticleMutation,
  useAdminHelpArticleByIdQuery,
  useAdminHelpCategoriesQuery,
  useAdminUpdateHelpArticleMutation,
} from '../../../features/admin/hooks/use-admin-api'
import {
  HelpArticleForm,
  type HelpArticleFormData,
} from '../../../features/help-center/components/help-article-form'

export const Route = createFileRoute('/admin/help-center/$id')({
  component: Page,
})

function Page() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const articleQuery = useAdminHelpArticleByIdQuery(id)
  const categoriesQuery = useAdminHelpCategoriesQuery()
  const updateMutation = useAdminUpdateHelpArticleMutation(id)
  const deleteMutation = useAdminDeleteHelpArticleMutation()

  const article = articleQuery.data?.data

  const categories = (categoriesQuery.data?.data?.items ?? []).map((category) => ({
    id: category.id,
    name: category.name,
  }))

  if (articleQuery.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">Loading...</div>
    )
  }

  if (!article) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        Help article not found
      </div>
    )
  }

  const showTemporaryMessage = (type: 'success' | 'error', text: string) => {
    setSaveMessage({ type, text })
    setTimeout(() => setSaveMessage(null), 3000)
  }

  const handleSubmit = (data: HelpArticleFormData) => {
    updateMutation.mutate(
      {
        path: { id },
        body: {
          title: data.title,
          slug: data.slug || undefined,
          content: data.content,
          description: data.description || null,
          categoryId: data.categoryId,
          status: data.status,
        },
      } as Options<V2AdminUpdateHelpArticleData>,
      {
        onSuccess: () => showTemporaryMessage('success', 'Saved'),
        onError: () => showTemporaryMessage('error', 'Failed to save'),
      }
    )
  }

  const handleTogglePublish = () => {
    const newStatus = article.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED'
    updateMutation.mutate(
      {
        path: { id },
        body: { status: newStatus },
      } as Options<V2AdminUpdateHelpArticleData>,
      {
        onSuccess: () =>
          showTemporaryMessage('success', newStatus === 'PUBLISHED' ? 'Published' : 'Unpublished'),
        onError: () => showTemporaryMessage('error', 'Failed to update status'),
      }
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(
      { path: { id } },
      {
        onSuccess: () => navigate({ to: '/admin/help-center' }),
      }
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      {/* Top bar with actions */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="min-w-0 truncate text-lg font-bold">{article.title}</h1>
        <div className="flex shrink-0 items-center gap-2">
          {showDeleteConfirm ? (
            <>
              <span className="text-sm text-red-400">Delete this article?</span>
              <SecondaryButton
                tone="danger"
                size="sm"
                onClick={handleDelete}
                loading={deleteMutation.isPending}
              >
                Confirm
              </SecondaryButton>
              <SecondaryButton size="sm" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </SecondaryButton>
            </>
          ) : (
            <>
              <SecondaryButton
                tone="danger"
                size="sm"
                icon={<Trash2 />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete
              </SecondaryButton>
              <SecondaryButton
                size="sm"
                onClick={handleTogglePublish}
                loading={updateMutation.isPending}
              >
                {article.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
              </SecondaryButton>
              <CtaButton
                size="sm"
                type="submit"
                form="help-article-edit-form"
                loading={updateMutation.isPending}
              >
                Save
              </CtaButton>
            </>
          )}
        </div>
      </div>

      <HelpArticleForm
        formId="help-article-edit-form"
        categories={categories}
        initialData={article}
        onSubmit={handleSubmit}
      />

      {saveMessage && (
        <div
          className={`fixed bottom-6 right-6 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            saveMessage.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {saveMessage.text}
        </div>
      )}
    </div>
  )
}
